import React, { useState } from 'react';
import { 
  useGetMenuQuery, 
  useCreateMenuItemMutation, 
  useUpdateMenuItemMutation, 
  useDeleteMenuItemMutation 
} from '../../services/api';
import { formatCurrency } from '../../utils/helpers';
import ProtectedRoute from '../../components/ProtectedRoute/ProtectedRoute';
import Loading from '../../components/Loading/Loading';
import Error from '../../components/Error/Error';
import './Menu.css';

const Menu = () => {
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: '',
    category: '',
    available: ''
  });

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    price: '',
    isAvailable: true
  });
  const [image, setImage] = useState(null);

  const { data, isLoading, error, refetch } = useGetMenuQuery(filters);
  const [createMenuItem] = useCreateMenuItemMutation();
  const [updateMenuItem] = useUpdateMenuItemMutation();
  const [deleteMenuItem] = useDeleteMenuItemMutation();

  const menuItems = data?.data?.menuItems || [];
  const categories = data?.data?.categories || [];
  const pagination = data?.data?.pagination;

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  const handleAddItem = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      category: '',
      price: '',
      isAvailable: true
    });
    setImage(null);
    setShowModal(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      category: item.category,
      price: item.price.toString(),
      isAvailable: item.isAvailable
    });
    setImage(null);
    setShowModal(true);
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this menu item?')) {
      try {
        await deleteMenuItem(itemId).unwrap();
      } catch (error) {
        console.error('Failed to delete menu item:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        image: image
      };

      if (editingItem) {
        await updateMenuItem({ id: editingItem._id, ...submitData }).unwrap();
      } else {
        await createMenuItem(submitData).unwrap();
      }

      setShowModal(false);
      setFormData({
        name: '',
        category: '',
        price: '',
        isAvailable: true
      });
      setImage(null);
      setEditingItem(null);
    } catch (error) {
      console.error('Failed to save menu item:', error);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      setImage(file);
    }
  };

  const toggleAvailability = async (item) => {
    try {
      await updateMenuItem({ 
        id: item._id, 
        isAvailable: !item.isAvailable 
      }).unwrap();
    } catch (error) {
      console.error('Failed to update availability:', error);
    }
  };

  if (isLoading) {
    return <Loading text="Loading menu..." />;
  }

  if (error) {
    return <Error message="Failed to load menu" onRetry={refetch} />;
  }

  return (
    <ProtectedRoute roles={['OWNER']}>
      <div className="container">
        <div className="page-header">
          <h1>Menu Management</h1>
          <div className="header-actions">
            <button onClick={handleAddItem} className="btn btn-primary">
              Add Menu Item
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="card">
          <div className="card-body">
            <form onSubmit={handleSearch} className="filters-form">
              <div className="filters-grid">
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Search by name or category..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="form-control"
                  />
                </div>
                
                <div className="form-group">
                  <select
                    value={filters.category}
                    onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                    className="form-control"
                  >
                    <option value="">All Categories</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <select
                    value={filters.available}
                    onChange={(e) => setFilters(prev => ({ ...prev, available: e.target.value }))}
                    className="form-control"
                  >
                    <option value="">All Items</option>
                    <option value="true">Available Only</option>
                    <option value="false">Unavailable Only</option>
                  </select>
                </div>

                <button type="submit" className="btn btn-primary">
                  Search
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Menu Items Table */}
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Availability</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {menuItems.map((item) => (
                  <tr key={item._id}>
                    <td>
                      <div className="menu-item-display">
                        {item.imageUrl && (
                          <img 
                            src={`${item.imageUrl}`} 
                            alt={item.name}
                            className="menu-item-image"
                          />
                        )}
                        <div>
                          <div className="item-name">{item.name}</div>
                          {item.imageUrl && (
                            <div className="text-sm text-muted">Has image</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>{item.category}</td>
                    <td>{formatCurrency(item.price)}</td>
                    <td>
                      <button
                        onClick={() => toggleAvailability(item)}
                        className={`availability-toggle ${item.isAvailable ? 'available' : 'unavailable'}`}
                      >
                        {item.isAvailable ? 'Available' : 'Unavailable'}
                      </button>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEditItem(item)}
                          className="btn btn-sm btn-secondary"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteItem(item._id)}
                          className="btn btn-sm btn-danger"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {menuItems.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center text-muted">
                      No menu items found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={filters.page === 1}
              className="btn btn-secondary"
            >
              Previous
            </button>
            
            <span className="pagination-info">
              Page {pagination.page} of {pagination.pages}
            </span>
            
            <button
              onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={filters.page === pagination.pages}
              className="btn btn-secondary"
            >
              Next
            </button>
          </div>
        )}

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h2>{editingItem ? 'Edit Menu Item' : 'Add Menu Item'}</h2>
                <button 
                  onClick={() => setShowModal(false)}
                  className="modal-close"
                >
                  ×
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="modal-body">
                <div className="form-group">
                  <label className="form-label">Item Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="form-control"
                    required
                    list="categories"
                  />
                  <datalist id="categories">
                    {categories.map(category => (
                      <option key={category} value={category} />
                    ))}
                  </datalist>
                </div>

                <div className="form-group">
                  <label className="form-label">Price (₹) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <input
                      type="checkbox"
                      checked={formData.isAvailable}
                      onChange={(e) => setFormData(prev => ({ ...prev, isAvailable: e.target.checked }))}
                      className="form-checkbox"
                    />
                    Available for ordering
                  </label>
                </div>

                <div className="form-group">
                  <label className="form-label">Item Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="form-control"
                  />
                  <div className="form-text">
                    Optional. Max file size: 5MB. Supported formats: JPG, PNG, JPEG
                  </div>
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingItem ? 'Update Item' : 'Add Item'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </ProtectedRoute>
  );
};

export default Menu;